import React from 'react';
import { Section, Container, Typography, Grid, Card, Anchor } from 'components/base';
import { CloudinaryPicture } from 'components/rich';
import { formatPrice } from 'utils/format';
import { ReferFriendShareCode } from 'ecommerce/components';
import withAuth from 'ecommerce/hoc/withAuth';
import useUserContext from 'ecommerce/context/user';

import { Head, Layout } from '../components/layout';
import { UserPageLink } from '../components/pageLinks';
import SignInPage from './signin';

const ReferFriendPage = () => {
  const userContext = useUserContext();

  return (
    <>
      <Head
        title="Give $10, Get $10"
        description="Share MyCanvas with your friends. They get $10 off their first order and you earn $10 in credit for each friend who makes a purchase."
        noindex
      />

      <Layout>
        <Section>
          <Container>
            <Typography.H1 className="section-title">Give $10, Get $10</Typography.H1>
            <Grid.Row className="justify-content-center">
              <Grid.Col xs={12} lg={9}>
                <div className="refer-friend-row">
                  <div className="refer-friend-left">
                    <Typography.H3 className="mb-2">Share and Earn MyCanvas Credit</Typography.H3>
                    <Typography.P className="text-body">
                      Know someone who will love MyCanvas as much as you do? Give them your special
                      code for $10 off their first order. For each friend that uses your discount
                      code, you’ll earn $10 in MyCanvas credit - up to $200!
                    </Typography.P>
                    <Typography.P className="text-body">
                      Referral code and earned credit are valid toward prints, books and display
                      only (not shipping cost) and are valid for 1 year.
                    </Typography.P>
                  </div>
                  <div className="refer-friend-right">
                    <CloudinaryPicture imageKey="hero/referfriendmc" />
                  </div>
                </div>
                <Typography.H2 className="text-center">It&apos;s Super Simple</Typography.H2>
                <ol className="list-ordered">
                  <li className="list-ordered-item">
                    <br />
                    Share your discount code with your genealogy loving friends and family.
                  </li>
                  <li className="list-ordered-item">
                    <br />
                    Your friends get $10 off their first order at MyCanvas.com.
                  </li>
                  <li className="list-ordered-item">
                    <br />
                    You get a $10 credit to print up future projects, each time your code is used.
                  </li>
                </ol>
                {!userContext.isLoading && (
                  <Grid.Row>
                    <Grid.Col xs={12} md={6} className="mx-auto">
                      <ReferFriendShareCode
                        value={userContext.data.referal_code}
                        url="https://www.mycanvas.com/"
                        subject="MyCanvas - My favorite photo printing app"
                        text={`I love #print_mycanvas - a photo printing app! Use the code ${userContext.data.referal_code} to get $10 off your first order. Grab the app at `}
                        title="Share your code with friends"
                        copyText="Copy to clipboard"
                      />
                      <Typography.H4 className="text-success mb-1">Your credit</Typography.H4>
                      <Card>
                        <Card.Body className="d-flex justify-content-between align-items-baseline">
                          <strong className="small font-weight-bold text-light text-uppercase mr-3">
                            Current Balance:{' '}
                          </strong>
                          <strong className="text-black">
                            {formatPrice(
                              userContext.data.credit_balance.amount,
                              userContext.data.credit_balance.currency,
                            )}
                          </strong>
                        </Card.Body>
                      </Card>
                      <div className="text-right small mt-1">
                        <UserPageLink section="credit">
                          <Anchor>View transaction history →</Anchor>
                        </UserPageLink>
                      </div>
                    </Grid.Col>
                  </Grid.Row>
                )}
                <Grid.Row>
                  <Grid.Col xs={12} md={6} className="mx-auto">
                    <div className="text-center small">
                      <Typography.P>
                        Need help? See our FAQ for more information on our{' '}
                        <Anchor
                          href="https://intercom.help/mycanvas/en/articles/6082658-refer-a-friend-program"
                          targetBlank
                        >
                          Refer a Friend Program
                        </Anchor>
                        .
                      </Typography.P>
                      <Typography.P>
                        Referral code and earned credit valid towards prints, books, and display
                        only (not shipping cost). Maximum 10 uses per referral code. Referral codes
                        valid for first time customers only. Earned credit valid for 1 year.
                      </Typography.P>
                    </div>
                  </Grid.Col>
                </Grid.Row>
              </Grid.Col>
            </Grid.Row>
          </Container>
        </Section>
      </Layout>
    </>
  );
};

// Crawlers hit this route unauthenticated and get the sign-in fallback — its
// "Login" head would leak onto /refer-friend, so re-assert the page's own
// title (rendered after SignInPage, the same head keys win) and keep noindex.
const ReferFriendSignIn = (props) => (
  <>
    <SignInPage {...props} />
    <Head
      title="Give $10, Get $10"
      description="Share MyCanvas with your friends. They get $10 off their first order and you earn $10 in credit for each friend who makes a purchase."
      noindex
    />
  </>
);

export default withAuth(ReferFriendPage, ReferFriendSignIn);
